/**
 * Utility functions for Laporan Tren 2 Data Pemantauan Jentik.
 */

/**
 * Check if a boolean-like value represents true.
 *
 * @param {any} val
 * @returns {boolean}
 */
export const isPositive = (val) => {
  return val === true || val === "true" || val === 1 || val === "1";
};

/**
 * Format a boolean-like value into "Ya" or "Tidak".
 *
 * @param {any} val
 * @returns {"Ya" | "Tidak"}
 */
export const formatBool = (val) => {
  return isPositive(val) ? "Ya" : "Tidak";
};

/**
 * Group array of visit records by `tp_id` preserving original appearance order.
 * Sort visits within each group by `kunjungan_ke` ascending.
 *
 * @param {Array<Object>} rows
 * @returns {Array<{ tp_id: any, info: Object, visits: Array<Object> }>}
 */
export const groupByTp = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const groups = [];
  const map = new Map();

  rows.forEach((row) => {
    const key =
      row?.tp_id != null ? String(row.tp_id) : `unknown_${groups.length}`;

    if (!map.has(key)) {
      const group = {
        tp_id: row?.tp_id,
        info: {
          provinsi: row?.provinsi || "-",
          kabupaten: row?.kabupaten || "-",
          fasyankes: row?.fasyankes || "-",
          desa: row?.desa || "-",
          nama_tp: row?.nama_tp || "-",
        },
        visits: [],
      };
      map.set(key, group);
      groups.push(group);
    }

    map.get(key).visits.push(row);
  });

  groups.forEach((group) => {
    group.visits.sort((a, b) => {
      const kA = parseInt(a?.kunjungan_ke, 10) || 0;
      const kB = parseInt(b?.kunjungan_ke, 10) || 0;
      return kA - kB;
    });
  });

  return groups;
};

/**
 * Build array-of-arrays export rows from container preserving group headers.
 *
 * @param {HTMLElement} container
 * @returns {Array<Array<any>>}
 */
export const buildExportRows = (container) => {
  if (!container || typeof container.querySelectorAll !== "function") {
    return [];
  }

  const elements = container.querySelectorAll("tr, [data-export-group]");
  const rows = [];
  const NUMBER_REGEX = /^-?\d+(\.\d+)?$/;

  elements.forEach((element) => {
    if (element.hasAttribute("data-export-group")) {
      rows.push([]);
      const children = Array.from(element.children).filter(
        (child) => child.tagName && child.tagName.toLowerCase() === "div"
      );
      children.forEach((child) => {
        const text = (child.textContent || "").trim();
        rows.push([text]);
      });
    } else if (element.tagName && element.tagName.toLowerCase() === "tr") {
      const cells = Array.from(element.children).filter((child) => {
        const tag = child.tagName ? child.tagName.toLowerCase() : "";
        return tag === "th" || tag === "td";
      });
      const row = cells.map((cell) => {
        const text = (cell.textContent || "").trim();
        return NUMBER_REGEX.test(text) ? Number(text) : text;
      });
      rows.push(row);
    }
  });

  return rows;
};

/**
 * Parse comma-separated fasyankes multi-select string into array of trimmed fasyankes codes.
 *
 * @param {string|Array<string>} selectedFasyankes
 * @returns {Array<string>}
 */
export const parseFasyankesCodes = (selectedFasyankes) => {
  if (!selectedFasyankes) return [];
  const raw = Array.isArray(selectedFasyankes)
    ? selectedFasyankes.join(",")
    : String(selectedFasyankes);

  return raw
    .split(",")
    .map((item) => item.split("|")[0].trim())
    .filter(Boolean);
};

/**
 * Filter larva identifications by list of fasyankes codes.
 *
 * @param {Array<Object>} identifications
 * @param {Array<string>} fasyankesCodes
 * @returns {Array<Object>}
 */
export const filterLarvaByFasyankes = (
  identifications = [],
  fasyankesCodes = []
) => {
  if (
    !Array.isArray(identifications) ||
    !Array.isArray(fasyankesCodes) ||
    fasyankesCodes.length === 0
  ) {
    return [];
  }
  const codesSet = new Set(fasyankesCodes);

  return identifications.filter((r) => {
    const code = String(r?.fasyankes_id || "").split("|")[0].trim();
    return codesSet.has(code);
  });
};

/**
 * Build distinct desa filter options from items with urbanvillage_id ("code|name").
 *
 * @param {Array<Object>} items
 * @returns {Array<{ label: string, value: string }>}
 */
export const getDesaFilterOptions = (items = []) => {
  if (!Array.isArray(items)) return [];

  const map = new Map();
  items.forEach((item) => {
    const raw = item?.urbanvillage_id;
    if (!raw) return;
    const parts = String(raw).split("|");
    const code = (parts[0] || "").trim();
    if (!code) return;
    const name = (parts[1] || "").trim() || code;
    if (!map.has(code)) {
      map.set(code, { label: name, value: code });
    }
  });

  return Array.from(map.values());
};

/**
 * Build distinct TP name filter options from larva identifications,
 * optionally narrowed to selectedDesa code.
 *
 * @param {Array<Object>} identifications
 * @param {string} selectedDesa
 * @returns {Array<{ label: string, value: string }>}
 */
export const getTpFilterOptions = (identifications = [], selectedDesa = "") => {
  if (!Array.isArray(identifications)) return [];

  const targetDesa = String(selectedDesa || "").trim();
  const set = new Set();

  identifications.forEach((r) => {
    const rawDesa = r?.urbanvillage_id;
    const desaCode = rawDesa ? String(rawDesa).split("|")[0].trim() : "";
    if (targetDesa && desaCode !== targetDesa) {
      return;
    }
    const name = (r?.name_breeding_place || "").trim();
    if (name) {
      set.add(name);
    }
  });

  return Array.from(set).map((name) => ({ label: name, value: name }));
};

